import React, { memo } from 'react';
import { Loader2 } from 'lucide-react';
import { IdealCustomerProfile, SignalSuggestion } from '../../types';
import { WizardMessage } from '../../types/suggestionWizard';
import { WIZARD_TEXT } from '../../constants/suggestionWizard';
import { TypewriterText } from '../common/TypewriterText';
import { OptionButtons } from './OptionButtons';
import { ChipSelector } from './ChipSelector';
import { ICPCarousel } from './ICPCarousel';
import { SignalList } from './SignalList';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

interface WizardMessagesProps {
  messages: WizardMessage[];
  isTyping: boolean;
  selectedChips: Set<string>;
  selectedSignals: Set<string>;
  savedSignalNames: Set<string>;
  expandedDescriptionId: string | null;
  expandedSignalName: string | null;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  lastUserMessageRef: React.RefObject<HTMLDivElement>;
  onOptionSelect: (value: string, label: string) => void;
  onChipToggle: (value: string) => void;
  onGenerateNow: () => void;
  onICPSelect: (icp: IdealCustomerProfile) => void;
  onExpandDescription: (id: string | null) => void;
  onToggleSignal: (signalName: string) => void;
  onSaveSignal: (e: React.MouseEvent, signal: SignalSuggestion) => void;
  onExpandSignal: (signalName: string | null) => void;
}

/**
 * Messages area for the wizard conversation.
 * Renders different message types: AI, user, options, chips, ICPs, signals.
 */
export const WizardMessages = memo<WizardMessagesProps>(({
  messages,
  isTyping,
  selectedChips,
  selectedSignals,
  savedSignalNames,
  expandedDescriptionId,
  expandedSignalName,
  messagesContainerRef,
  messagesEndRef,
  lastUserMessageRef,
  onOptionSelect,
  onChipToggle,
  onGenerateNow,
  onICPSelect,
  onExpandDescription,
  onToggleSignal,
  onSaveSignal,
  onExpandSignal,
}) => {
  // Find the last user message ID for ref attachment
  const userMessages = messages.filter(m => m.type === 'user');
  const lastUserMessageId = userMessages.length > 0 ? userMessages[userMessages.length - 1].id : null;

  return (
    <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50/30">
      {messages.map((message) => {
        // Skip rendering empty messages
        if (message.type === 'icps' && (!message.icps || message.icps.length === 0)) return null;
        if (message.type === 'options' && (!message.options || message.options.length === 0)) return null;
        if (message.type === 'chips' && (!message.chips || message.chips.length === 0)) return null;
        if (message.type === 'signals' && (!message.signals || message.signals.length === 0)) return null;

        return (
          <div key={message.id}>
            {/* AI Message */}
            {message.type === 'ai' && (
              <div className="flex gap-3 group">
                <img
                  src={skylarqLogo}
                  alt=""
                  className="w-7 h-7 object-contain flex-shrink-0"
                />
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                    {WIZARD_TEXT.BRAND_NAME_UPPER}
                  </p>
                  <p className="text-[15px] text-gray-800 leading-relaxed">
                    {message.animate ? (
                      <TypewriterText text={message.content} speed={12} />
                    ) : (
                      message.content
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* User Message */}
            {message.type === 'user' && (
              <div
                ref={message.id === lastUserMessageId ? lastUserMessageRef : undefined}
                data-user-message={message.id}
                data-is-last={message.id === lastUserMessageId ? 'true' : 'false'}
                className="flex justify-end"
              >
                <div className="max-w-[80%] bg-gray-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5">
                  <p className="text-[14px] leading-relaxed">{message.content}</p>
                </div>
              </div>
            )}

            {/* Options */}
            {message.type === 'options' && message.options && (
              <OptionButtons options={message.options} onSelect={onOptionSelect} />
            )}

            {/* ICP Carousel */}
            {message.type === 'icps' && message.icps && message.icps.length > 0 && (
              <ICPCarousel
                icps={message.icps}
                expandedDescriptionId={expandedDescriptionId}
                onExpandDescription={onExpandDescription}
                onSelectIcp={onICPSelect}
              />
            )}

            {/* Chips */}
            {message.type === 'chips' && message.chips && (
              <ChipSelector
                chips={message.chips}
                selectedChips={selectedChips}
                onChipToggle={onChipToggle}
                onSkip={onGenerateNow}
                showSkip
              />
            )}

            {/* Text Input hint */}
            {message.type === 'text_input' && (
              <div className="mt-1">
                <p className="text-[11px] text-gray-400">
                  {message.inputPlaceholder || 'Type in the input below...'}
                </p>
                <button
                  onClick={onGenerateNow}
                  className="mt-1.5 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {WIZARD_TEXT.SKIP_GENERATE}
                </button>
              </div>
            )}

            {/* Signals */}
            {message.type === 'signals' && message.signals && (
              <SignalList
                signals={message.signals}
                selectedSignals={selectedSignals}
                savedSignalNames={savedSignalNames}
                expandedSignalName={expandedSignalName}
                onToggleSignal={onToggleSignal}
                onSaveSignal={onSaveSignal}
                onExpandToggle={onExpandSignal}
              />
            )}
          </div>
        );
      })}

      {/* Typing indicator */}
      {isTyping && (
        <div className="flex gap-3">
          <img
            src={skylarqLogo}
            alt=""
            className="w-7 h-7 object-contain flex-shrink-0"
          />
          <div className="flex-1 min-w-0 pt-0.5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
              {WIZARD_TEXT.BRAND_NAME_UPPER}
            </p>
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-[14px]">{WIZARD_TEXT.THINKING}</span>
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />

      {/* Spacer to allow scrolling user message to top */}
      <div className="min-h-[60vh]" />
    </div>
  );
});

WizardMessages.displayName = 'WizardMessages';

export default WizardMessages;
